import React, { useMemo, useState } from "react"
import moment from "moment"
import { useNavigate } from "react-router-dom"
import { DATE_FORMAT } from "modules/datepicker/constants"
import { routes } from "router"
import IconWithBg from "ui/IconWithBg"
import { getJournalIcon, overlaps, parseTime, parseTimeOffRanges } from "pagesV2/pro/Calendar/utils"
import styles from "../index.module.css"
import { PlusCircleOutlined } from "@ant-design/icons"
import { spacings } from "themes/variables"
import { useTranslation } from "react-i18next"
import { useDevice } from "hooks/useDevice"
import { Button, Flex } from "antd"
import { Text } from "ui/Typography"
import TimeOffTooltip from "../TimeOffTooltip"
import { EllipsisIcon } from "icons"
import BookingItem from "../BookingItem"
import { genCalendarStyles } from "antd/es/calendar/style"
import useActiveGuide from "hooks/useActiveClient"

function renderOverlays(timeOffRanges, allDayTimeOff) {
  return (
    <>
      {timeOffRanges?.length ? <div aria-hidden className={styles.surfaceOverlay} /> : null}
      {allDayTimeOff ? <div aria-hidden className={styles.allDayOverlay} /> : null}
    </>
  )
}

function renderHeaderIcons({
  isMobile,
  isTablet,
  active,
  journalNoteDates,
  formattedDate,
  handleJournalModalOpen,
  handleAddBooking,
  isPastDay,
  allDayTimeOff
}) {
  if (isMobile || isTablet) return null
  return (
    <Flex className={styles.iconHover}>
      <IconWithBg
        disabled={!active}
        icon={getJournalIcon(journalNoteDates, formattedDate)}
        onClick={active ? () => handleJournalModalOpen(formattedDate) : undefined}
      />
      {!isPastDay && !allDayTimeOff && (
        <IconWithBg
          disabled={!active}
          icon={<PlusCircleOutlined color="var(--primary-color)" />}
          onClick={active ? handleAddBooking : undefined}
          className={styles.addIcon}
        />
      )}
    </Flex>
  )
}

function renderViewMoreButton({ renderList, onChangeFilters, formattedDate, isMobile, isTablet, t }) {
  if ((renderList?.length ?? 0) <= 4) return null
  return (
    <Button
      className={styles.viewMoreButton}
      type="link"
      onClick={() => onChangeFilters({ target: { name: "range", value: "date" }, extra: { name: "date", value: formattedDate } })}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onChangeFilters({ target: { name: "range", value: "date" }, extra: { name: "date", value: formattedDate } })
        }
      }}
    >
      {isMobile || isTablet ? <EllipsisIcon height={18} width={18} fill="var(--color-sportsman-brand)" /> : t("calendar.view_more")}
    </Button>
  )
}

function renderEventsList({ renderList, onChangeFilters, formattedDate, isMobile, isTablet, t }) {
  if (!renderList?.length) return null
  return (
    <Flex vertical className="z-1 mt-3xl overflow-hidden" gap={spacings["S"]}>
      {renderList.slice(0, 4)}
      {renderViewMoreButton({ renderList, onChangeFilters, formattedDate, isMobile, isTablet, t })}
    </Flex>
  )
}

function renderDayCellContent({
  timeOffRanges,
  allDayTimeOff,
  formattedDate,
  journalNoteDates,
  handleJournalModalOpen,
  handleAddBooking,
  day,
  renderList,
  onChangeFilters,
  isMobile,
  isTablet,
  active,
  isPastDay,
  t
}) {
  return (
    <>
      {renderOverlays(timeOffRanges, allDayTimeOff)}
      {!allDayTimeOff && timeOffRanges?.length ? (
        <TimeOffTooltip
          timeOffRanges={timeOffRanges}
          formattedDate={formattedDate}
          journalNoteDates={journalNoteDates}
          handleJournalModalOpen={handleJournalModalOpen}
          handleAddBooking={handleAddBooking}
          day={day}
          renderList={renderList}
          onChangeFilters={onChangeFilters}
        />
      ) : (
        <div className={styles.timeOffContainer}>
          <Flex justify="center" align="center">
            <Text>{day.format("DD")}</Text>
            {renderHeaderIcons({
              isMobile,
              isTablet,
              active,
              journalNoteDates,
              formattedDate,
              handleJournalModalOpen,
              handleAddBooking,
              isPastDay,
              allDayTimeOff
            })}
          </Flex>
          {renderEventsList({ renderList, onChangeFilters, formattedDate, isMobile, isTablet, t })}
        </div>
      )}
    </>
  )
}

export const useDayCell = ({
  day,
  timeOffRanges = [],
  allDayTimeOff = false,
  bookingsData = [],
  journalNoteDates = [],
  onChangeFilters
}) => {
  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)

  const navigate = useNavigate()
  const { t } = useTranslation()
  const formattedDate = day.format(DATE_FORMAT)
  const { isMobile, isTablet } = useDevice()
  const [active] = useActiveGuide()

  const isPastDay = useMemo(() => day.isBefore(moment().startOf("day"), "day"), [day])
  const parsedTimeOff = useMemo(() => parseTimeOffRanges(timeOffRanges || []), [timeOffRanges])

  // Use bookingsData passed as prop instead of fetching from Redux
  const dayBookings = useMemo(() => bookingsData || [], [bookingsData])

  const renderList = useMemo(() => {
    return [...dayBookings]
      .sort((a, b) => {
        const startTimeA = moment(a?.formatted_start_time, "hh:mm A", true)
        const startTimeB = moment(b?.formatted_start_time, "hh:mm A", true)
        const isValidA = startTimeA.isValid()
        const isValidB = startTimeB.isValid()
        // If both are valid, compare normally
        if (isValidA && isValidB) {
          const startTimeDiff = startTimeA.diff(startTimeB)
          if (startTimeDiff !== 0) return startTimeDiff
        } else if (isValidA) {
          return -1 // a has a valid time, b does not
        } else if (isValidB) {
          return 1 // b has a valid time, a does not
        } else {
          return 0 // neither has a valid time
        }

        // If start times are equal, compare end times
        const endTimeA = moment(a?.formatted_end_time, "hh:mm A", true)
        const endTimeB = moment(b?.formatted_end_time, "hh:mm A", true)
        const isEndValidA = endTimeA.isValid()
        const isEndValidB = endTimeB.isValid()
        if (isEndValidA && isEndValidB) {
          return endTimeA.diff(endTimeB)
        } else if (isEndValidA) {
          return -1
        } else if (isEndValidB) {
          return 1
        } else {
          return 0
        }
      })
      .map((booking) => {
        const bStart = parseTime(booking?.formatted_start_time)
        const bEnd = parseTime(booking?.formatted_end_time)
        const hasConflict = Boolean(allDayTimeOff) || parsedTimeOff.some((r) => overlaps(bStart, bEnd, r.start, r.end))
        return (
          <BookingItem
            key={booking?.id}
            booking={booking}
            conflict={hasConflict}
            setIsBookingModalOpen={setIsBookingModalOpen}
            setSelectedBookingId={setSelectedBookingId}
            className={genCalendarStyles.eventItem}
          />
        )
      })
  }, [dayBookings, parsedTimeOff, allDayTimeOff])

  const handleJournalModalOpen = (date) => {
    setIsJournalModalOpen(true)
    setSelectedDate(date)
  }

  const handleCancel = () => {
    setIsJournalModalOpen(false)
  }

  const handleAddBooking = () => {
    if (allDayTimeOff) return
    navigate(routes.guideBookingNewPath())
  }

  const handleBookingCancel = () => {
    setIsBookingModalOpen(false)
    setSelectedBookingId(null)
  }

  const setJournalOpen = (open) => setIsJournalModalOpen(!!open)

  const content = renderDayCellContent({
    timeOffRanges,
    allDayTimeOff,
    formattedDate,
    journalNoteDates,
    handleJournalModalOpen,
    handleAddBooking,
    day,
    renderList,
    onChangeFilters,
    isMobile,
    isTablet,
    active,
    isPastDay,
    t
  })

  return {
    // state
    isJournalModalOpen,
    isBookingModalOpen,
    selectedDate,
    selectedBookingId,

    // derived
    formattedDate,
    isPastDay,

    // content
    content,

    // handlers
    handleCancel,
    handleBookingCancel,
    setJournalOpen
  }
}
